import { useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { UserCheck, UserX, Clock, User, Building2, Mail, Shield, Briefcase } from "lucide-react";
import { toast } from "sonner";

const AdminApprovals = () => {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<"pending" | "approved" | "all">("pending");

  const { data: pendingProfiles = [], isLoading } = useQuery({
    queryKey: ["admin-approvals", filter],
    queryFn: async () => {
      let query = supabase
        .from("profiles")
        .select("*, companies(name)")
        .order("created_at", { ascending: false });

      if (filter === "pending") query = query.eq("is_approved", false);
      else if (filter === "approved") query = query.eq("is_approved", true);

      const { data } = await query;
      return data ?? [];
    },
  });

  const { data: roles = [] } = useQuery({
    queryKey: ["admin-approval-roles"],
    queryFn: async () => {
      const { data } = await supabase.from("user_roles").select("*");
      return data ?? [];
    },
  });

  const approveMutation = useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await supabase
        .from("profiles")
        .update({ is_approved: true })
        .eq("user_id", userId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-approvals"] });
      toast.success("Account approved");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const revokeMutation = useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await supabase
        .from("profiles")
        .update({ is_approved: false })
        .eq("user_id", userId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-approvals"] });
      toast.success("Account access revoked");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const getRoleLabel = (userId: string) => {
    const role = roles.find((r) => r.user_id === userId);
    return role?.role ?? "client";
  };

  const roleBadge: Record<string, { bg: string; icon: typeof Shield }> = {
    admin: { bg: "bg-teal/15 text-teal", icon: Shield },
    employee: { bg: "bg-lime/15 text-lime", icon: Briefcase },
    client: { bg: "bg-primary-foreground/8 text-primary-foreground/40", icon: User },
  };

  const pendingCount = pendingProfiles.filter((p) => !(p as any).is_approved).length;

  const filters = [
    { key: "pending" as const, label: "Pending", count: filter === "pending" ? pendingProfiles.length : undefined },
    { key: "approved" as const, label: "Approved" },
    { key: "all" as const, label: "All" },
  ];

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-teal/60 text-[11px] font-bold tracking-[4px] uppercase font-body mb-2">Admin</p>
          <h1 className="font-display text-3xl text-primary-foreground tracking-tight">
            Account <span className="accent-text">Approvals</span>
          </h1>
          <p className="text-primary-foreground/40 font-body text-sm mt-1">
            Review and approve client & employee account registrations
          </p>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 mb-6 bg-primary-foreground/3 rounded-xl p-1 w-fit">
        {filters.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 text-[11px] font-bold tracking-[1.5px] uppercase font-body rounded-lg transition-all ${
              filter === f.key
                ? "bg-teal/15 text-teal"
                : "text-primary-foreground/40 hover:text-primary-foreground/60"
            }`}
          >
            {f.label}
            {f.key === "pending" && filter === "pending" && pendingProfiles.length > 0 && (
              <span className="ml-2 px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 rounded-md text-[10px]">
                {pendingProfiles.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" />
        </div>
      ) : pendingProfiles.length === 0 ? (
        <div className="glass-card rounded-2xl p-16 text-center">
          <UserCheck className="w-12 h-12 text-primary-foreground/15 mx-auto mb-4" />
          <p className="text-primary-foreground/40 font-body">
            {filter === "pending" ? "No pending approvals" : "No accounts found"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {pendingProfiles.map((profile) => {
            const role = getRoleLabel(profile.user_id);
            const badge = roleBadge[role] || roleBadge.client;
            const BadgeIcon = badge.icon;
            const isApproved = (profile as any).is_approved;

            return (
              <div
                key={profile.id}
                className={`glass-card rounded-2xl p-5 transition-all ${
                  !isApproved ? "border-yellow-500/20" : ""
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  {/* User info */}
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      isApproved ? "bg-teal/10" : "bg-yellow-500/10"
                    }`}>
                      {isApproved ? (
                        <UserCheck className="w-5 h-5 text-teal" />
                      ) : (
                        <Clock className="w-5 h-5 text-yellow-400" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-sm text-primary-foreground font-body font-medium truncate">
                          {profile.full_name || "Unnamed User"}
                        </p>
                        <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${badge.bg}`}>
                          <BadgeIcon className="w-3 h-3 inline mr-1" />
                          {role}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-primary-foreground/30 font-body">
                        <span className="flex items-center gap-1 truncate">
                          <Mail className="w-3 h-3" />
                          {profile.user_id.slice(0, 8)}...
                        </span>
                        <span className="flex items-center gap-1 truncate">
                          <Building2 className="w-3 h-3" />
                          {(profile.companies as any)?.name || "Unassigned"}
                        </span>
                        <span className="hidden sm:inline">
                          {new Date(profile.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 sm:flex-shrink-0">
                    {!isApproved ? (
                      <>
                        <button
                          onClick={() => approveMutation.mutate(profile.user_id)}
                          disabled={approveMutation.isPending}
                          className="flex items-center gap-1.5 px-4 py-2 bg-teal/15 text-teal text-[11px] font-bold tracking-[1px] uppercase rounded-xl hover:bg-teal/25 transition-all font-body disabled:opacity-50"
                        >
                          <UserCheck className="w-3.5 h-3.5" />
                          Approve
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => revokeMutation.mutate(profile.user_id)}
                        disabled={revokeMutation.isPending}
                        className="flex items-center gap-1.5 px-4 py-2 bg-destructive/10 text-destructive text-[11px] font-bold tracking-[1px] uppercase rounded-xl hover:bg-destructive/20 transition-all font-body disabled:opacity-50"
                      >
                        <UserX className="w-3.5 h-3.5" />
                        Revoke
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminApprovals;
